import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Play, ArrowRight } from "lucide-react";
import RoutingWidget from "@/components/RoutingWidget";
import { scrollToId } from "@/lib/smoothScroll";
import { EASE } from "@/lib/motion";

const LINES = ["Kill the “status?” loop.", "Keep the SLA."];

export default function Hero({ onPlay }) {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const widgetY = useTransform(scrollYProgress, [0, 1], [0, reduced ? 0 : -60]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative overflow-hidden pt-[70px]"
    >
      {/* quiet engineering-grid texture, concentrated at top */}
      <div className="pointer-events-none absolute inset-0 grid-texture opacity-[0.55]" />
      <div className="pointer-events-none absolute -top-40 left-1/2 h-[520px] w-[820px] -translate-x-1/2 rounded-full bg-signal/[0.07] blur-[130px]" />

      <div className="container-max band-pad relative grid grid-cols-1 items-center gap-14 py-16 lg:grid-cols-[1.05fr_0.95fr] lg:gap-10 lg:py-24">
        {/* left — copy */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="mb-7 inline-flex items-center gap-2 rounded-full border border-hairline bg-bg-surface/60 px-3.5 py-1.5"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-signal" />
            <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-muted">
              AI ops layer · seed-stage
            </span>
          </motion.div>

          <h1 className="font-display text-4xl font-semibold leading-[1.04] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            {LINES.map((line, i) => (
              <span key={i} className="mask-line">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.14, ease: EASE }}
                >
                  {line.includes("SLA") ? (
                    <>
                      Keep the <span className="text-signal">SLA.</span>
                    </>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5, ease: EASE }}
            className="mt-6 max-w-xl text-base leading-relaxed text-ink-muted md:text-lg"
          >
            RelayOps is the AI ops layer that routes work, enforces your SOPs, and
            answers “who owns this?” before anyone has to ask — built for ops teams
            running logistics, manufacturing, field service, and supply chain.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.62, ease: EASE }}
            className="mt-9 flex flex-col items-start gap-4 sm:flex-row sm:items-center"
          >
            <button
              data-testid="hero-play-cta"
              onClick={onPlay}
              className="group inline-flex items-center gap-2.5 rounded-full bg-signal px-6 py-3.5 font-sans text-sm font-semibold text-bg-primary transition-transform duration-200 hover:scale-[1.03] active:scale-95"
            >
              <span className="grid h-5 w-5 place-items-center rounded-full bg-bg-primary/15">
                <Play size={11} fill="currentColor" />
              </span>
              See RelayOps demo
            </button>
            <button
              data-testid="hero-audit-link"
              onClick={() => scrollToId("contact")}
              className="group inline-flex items-center gap-1.5 font-sans text-sm font-medium text-ink transition-colors hover:text-signal"
            >
              Get a workflow audit
              <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
            </button>
          </motion.div>
        </div>

        {/* right — signature widget */}
        <motion.div style={{ y: widgetY }} className="relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.35, ease: EASE }}
          >
            <RoutingWidget />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
